import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { closeModal } from 'actions/modal';

import { patch as patchCourseClassLog } from 'actions/course-class-log';
import { invalidateItem as invalidateAccountDetails } from 'actions/student-account';

import { store } from 'redux-rest-reducer';
import PropTypes from 'prop-types';

import styled from 'styled-components';
import { Button, Separator, Text } from '@cuemath/comps';

import {
  Message,
  Head,
  FieldWrapper,
  renderFormField as renderFormFieldModule,
} from 'modules/form';
import {
  FIELD_TYPES,
  getOnChangeMethod,
  COURSE_CLASS_LOG_ACTION_FORM_FIELDS,
  COURSE_CLASS_LOGS_ACTION_OPTIONS,
} from 'constants/form';
import COLORS from 'constants/colors';

const { ABBEY } = COLORS;
const HeadWrapper = styled(Head)`
  border-bottom: 1px solid ${ABBEY};
`;

class CourseClassLogActionModal extends PureComponent {
  static propTypes = {
    actions: PropTypes.shape({
      closeModal: PropTypes.func.isRequired,
      patchCourseClassLog: PropTypes.func.isRequired,
    }).isRequired,
    courseClassLog: PropTypes.shape({}).isRequired,
    classLogData: PropTypes.shape({
      classDate: PropTypes.string.isRequired,
      classTime: PropTypes.string.isRequired,
      classType: PropTypes.string.isRequired,
      logId: PropTypes.string.isRequired,
    }).isRequired,
  };

  constructor(props) {
    super(props);
    this.state = {
      errorText: null,
      message: null,
      form: this.getFormDefaults(),
      classLogPatch: store.patch(),
      patchInitiated: false,
      formFields: COURSE_CLASS_LOG_ACTION_FORM_FIELDS,
    };
    this.assignChangeHandlers(COURSE_CLASS_LOG_ACTION_FORM_FIELDS);
  }

  static getDerivedStateFromProps(props, state) {
    const { courseClassLog, actions, studentId } = props;
    const { classLogPatch, patchInitiated } = state;
    const { id: patchId } = classLogPatch || {};
    const newState = {};

    const courseClassLogData = store.patch(courseClassLog, patchId);
    const { resolved } = courseClassLogData || {};
    if (resolved && patchInitiated) {
      const { isPatched, isPatchingFailed } = resolved || {};
      if (isPatched) {
        newState.message = 'Course Log successfully updated';
        newState.errorText = null;
        actions.invalidateAccountDetails(studentId);
        setTimeout(() => actions.closeModal(), 1000);
      } else if (isPatchingFailed) {
        const error = store.getError(resolved);
        newState.errorText = error ? error.message : 'Update failed, please try again';
        newState.message = null;
      }
      newState.patchInitiated = false;
    }
    return newState;
  }

  assignChangeHandlers = formFields => {
    formFields.forEach(({ name, fieldType }) => {
      const methodName = getOnChangeMethod(name);
      if (fieldType === FIELD_TYPES.INPUT) {
        this[methodName] = this.onChange.bind(this, name);
      } else {
        this[methodName] = this.onChange.bind(this);
      }
    });
  };

  getOptions = name => {
    switch (name) {
      case 'action':
        return COURSE_CLASS_LOGS_ACTION_OPTIONS;
      default:
        return [];
    }
  };

  getFormDefaults = () => {
    const formDefaults = {};
    const { classLogData } = this.props;

    COURSE_CLASS_LOG_ACTION_FORM_FIELDS.forEach(field => {
      const { fieldType, name, default: defaultValue } = field || {};
      if (fieldType === FIELD_TYPES.SELECT) {
        field.choices = this.getOptions(name);
      } else {
        formDefaults[name] =
          !!classLogData && classLogData[name] ? classLogData[name] : defaultValue;
      }
    });

    return formDefaults;
  };

  onChange = (key, value) => {
    const { form } = this.state;

    const newForm = { ...form };
    newForm[key] = value;

    const stateChanges = { errorText: null, form: newForm };
    this.setState(() => ({ ...stateChanges }));
  };

  renderFormField = field => {
    const { form } = this.state;
    const {
      disabled,
      editable,
      required,
      placeholder,
      label,
      name,
      default: defaultValue,
    } = field || {};
    const fieldDisabled = disabled || !editable;
    const fieldPlaceholder = disabled ? '' : placeholder || label;

    return renderFormFieldModule({
      ...field,
      value: form[name],
      onChange: this[getOnChangeMethod(name)],
      defaultValue,
      required,
      disabled: fieldDisabled,
      placeholder: fieldPlaceholder,
    });
  };

  checkRequiredFields = event => {
    event.preventDefault();
    const { formFields, form } = this.state;
    let isValid = true;
    formFields.every(({ required, name, label }) => {
      if (required && name && !form[name]) {
        isValid = false;
        this.setState(
          { errorText: `${label} is a required field` },
          this.updateStudentCourse,
        );
        return false;
      }
      return true;
    });
    if (isValid) this.updateStudentCourse();
  };

  getPatchData = state => {
    const { form } = state;

    const data = {
      action: form['action'],
      action_data: {
        reason: form['reason'],
      },
    };

    return data;
  };

  updateStudentCourse = () => {
    const { classLogData, actions } = this.props;
    const { logId } = classLogData || {};
    const {
      classLogPatch: { id, current },
      errorText,
    } = this.state;

    if (errorText) return;

    if (current && current.isPatching) return;

    this.setState({ patchInitiated: true }, () => {
      actions.patchCourseClassLog(id, logId, this.getPatchData(this.state));
    });
  };

  renderFormFields = () => {
    const { formFields, errorText, message } = this.state;
    let renderedFields = formFields.map(field => this.renderFormField(field));

    return (
      <FieldWrapper>
        {renderedFields}
        {errorText && <Message error={true}>{errorText}</Message>}
        {message && <Message>{message}</Message>}
        <Separator transparent height="25" />
        <Button label="Submit" onClick={event => this.checkRequiredFields(event)} />
        <Separator transparent height="20" />
      </FieldWrapper>
    );
  };

  render() {
    const { classLogData } = this.props;
    const { classTakenOn, classType } = classLogData || {};

    return (
      <>
        <HeadWrapper>
          <Text size="xlarge" align="center">
            Course Class Log Edit
          </Text>
        </HeadWrapper>
        <Separator transparent height="10" />

        {this.renderFormFields(classTakenOn, classType)}
      </>
    );
  }
}
export default connect(
  ({ courseClassLog }) => ({
    courseClassLog,
  }),
  dispatch => ({
    actions: bindActionCreators(
      {
        closeModal,
        invalidateAccountDetails,
        patchCourseClassLog,
      },
      dispatch,
    ),
  }),
)(CourseClassLogActionModal);
